/**
 * Store pour les statuts des plannings générés par l'IA du client connecté
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';
/***/
import { useAuthUserStore } from '@stores/auth';

export const useAuthCustomerPlanningStatusStore = defineStore('authCustomerPlanningStore', (() => {

    // Store pour mettre à jour les données de l'utilisateur connecté
    const authUserStore = useAuthUserStore();

    // State des statuts de planning chargés
    const planningStatuses = ref([]);

    // Idées de sujets de publication par planning
    const subjectsIdeasByPlanning = ref({});

    // Date de dernière génération des idées par planning
    const lastIdeaGenerationByPlanning = ref({});

    // Date de chargement de la liste des tâches
    const initializationTaskListDateByPlanning = ref({});

    /**
     * Retourne la date d'initialisation des tâches du planning en paramètre
     * @return object|null
     */
    const getPlanningStatusInitializationTaskListDate = computed(() => {
        return ((planning) => {
            const date = (initializationTaskListDateByPlanning.value[planning.id] ?? null);
            return date;
        });
    });

    /**
     * Retourne le statut du planning de l'année et du mois en paramètre
     * @param object user
     * @param int year
     * @param int month
     * @return object|null
     */
    const getPlanningStatusByPeriod = computed(() => {
        return ((user, year, month) => {
            const targetedStatus = planningStatuses.value.find((planningStatus) =>
                planningStatus.userId === user.id &&
                planningStatus.year === year &&
                planningStatus.month === month
            );

            return (targetedStatus ?? null);
        });
    });

    /**
     * Retourne les données de dernière génération des idées pour le planning en paramètre
     * @param object planning
     * @return object
     */
    const getLastPlanningIdeaGenerationData = computed(() => {
        return ((planning) => {
            const data = (lastIdeaGenerationByPlanning.value[planning.id] ?? {});
            data.start ??= null;
            data.end ??= null;
            data.totalExpected ??= 0;
            data.ideasFound ??= 0;
            return data;
        });
    });

    /**
     * Retourne les idées de sujets de publication du planning en paramètre
     * @param object planning
     * @return object|null
     */
    const getSubjectsIdeasByPlanning = computed(() => {
        return ((planning) => {
            return (subjectsIdeasByPlanning.value[planning.id] ?? []);
        });
    });

    /**
     * Mise à jour du store après la suppression du sujet du planning en paramètre
     * @param object planning
     * @param object subject
     * @return void
     */
    const onRemoveSubjectOfPlanning = ((planning, subject) => {
         const subjects = (subjectsIdeasByPlanning.value[planning.id] ?? []);

         const subjectIndexToDelete = subjects.findIndex((currentSubject) => currentSubject.id === subject.id);

         if(subjectIndexToDelete === -1) {
             console.log('Le sujet est introuvable.');
             return;
         }

        subjectsIdeasByPlanning.value[planning.id].splice(subjectIndexToDelete, 1);
    });

    /**
     * Modifie la date d'initialisation des tâches du planning en paramètre
     * @param object planning
     * @param object date
     * @return void
     */
    const setPlanningStatusInitializationTaskListDate = ((planning, date) => {
        initializationTaskListDateByPlanning.value[planning.id] = date;
    });

    /**
     * Met à jour le statut du planning dans le store
     * @param object planningStatus
     * @return void
     */
    const updatePlanningStatus = ((planningStatus) => {

        const existingIndex = planningStatuses.value.findIndex((currentStatus) => currentStatus.id === planningStatus.id);

        if(existingIndex !== -1) {
            const existingStatus = planningStatuses.value[existingIndex];
            planningStatuses.value[existingIndex] = {
                ...existingStatus,
                ...planningStatus
            };
        } else {
            planningStatuses.value.push(planningStatus);
        }

    });

    /**
     * Génération de nouvelles idées de tâche
     * @param object planningStatus
     * @param int numberIdeas Nombre d'idées à générer
     * @rekturn Promise
     */
    const generateNewIdeas = ((planningStatus, numberIdeas) => {

        const path = '/api/me/tasks/planning/:planningId/generate-new-tasks'.replace(':planningId', planningStatus.id);

        initNewGenerationIdeas(planningStatus, numberIdeas);

        return axios.post(path).then((response) => {

            lastIdeaGenerationByPlanning.value[response.data.planningStatus.id].end = new Date();
            lastIdeaGenerationByPlanning.value[response.data.planningStatus.id]


            if(response && response.data && response.data.success) {

                if(response.data.countTasks) {
                    lastIdeaGenerationByPlanning.value[response.data.planningStatus.id].ideasFound = response.data.countTasks;
                }

                if(response.data.planningStatus) {
                    updatePlanningStatus(response.data.planningStatus);
                    return Promise.resolve();
                }
            }
            return Promise.reject();
        });
    });

    /**
     * Génération du planning
     * @param object planningStatus
     * @return Promise
     */
    const generate = ((planningStatus) => {

        const path = '/api/me/tasks/planning/:planningId/generate'.replace(':planningId', planningStatus.id);

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success) {

                if(response.data.user) {
                    authUserStore.updateCurrentUserData(response.data.user);
                }

                if(response.data.planningStatus) {
                    // Ajout d'un timer, pour exécuter les événements dans les composants
                    setTimeout(() => {
                        updatePlanningStatus(response.data.planningStatus);
                    }, 100);

                    return Promise.resolve();
                }
            }
            return Promise.reject();
        });

    });

    /**
     * Complétion du planning
     * @param object planningStatus
     * @return Promise
     */
    const complete = ((planningStatus) => {

        const path = '/api/me/tasks/planning/:planningId/complete'.replace(':planningId', planningStatus.id);

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.planningStatus) {

                // Ajout d'un timer, pour exécuter les événements dans les composants
                setTimeout(() => {
                    updatePlanningStatus(response.data.planningStatus);
                }, 100);

                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Chargement des idées de sujets de publication du planning
     * @param Object requestParams (year, month, excludeIds)
     * @param bool reset Vrai s'il faut réinitialiser la liste
     * @return Promise
     */
    const loadSubjectsIdeasOfPlanning = ((requestParams, reset) => {
        const path = `/api/me/tasks/planning/subjects-ideas`;

        return axios.get(path, {
            params: requestParams
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.subjects && response.data.planningStatus) {
                const planning = response.data.planningStatus;
                updatePlanningStatus(planning);

                if(reset) {
                    subjectsIdeasByPlanning.value[planning.id] = [];
                }

                response.data.subjects.forEach((subject) => {
                    updatePlanningSubjectIdea(planning, subject);
                });

                return Promise.resolve();
            }
            return Promise.reject();
        });
    });

    /**
     * Mise à jour d'un sujet dans les idées de sujets d'un planning
     * @param Object planning
     * @param Object subject
     * @return void
     */
    const updatePlanningSubjectIdea = ((planning, subject) => {

        subjectsIdeasByPlanning.value[planning.id] ??= [];
        const currentSubjects = (subjectsIdeasByPlanning.value[planning.id] ?? []);

        const subjectIndex = currentSubjects.findIndex((subjectFromStore) => subjectFromStore.id === subject.id);
        if(subjectIndex === -1) {
            subjectsIdeasByPlanning.value[planning.id].push(subject);
        } else {
            subjectsIdeasByPlanning.value[planning.id] = { ...subject };
        }

    });

    /**
     * Convertit les idées de sujets en paramètre en idées de tâche
     * @param object planningStatus
     * @param Array subjects
     * @return Promise
     */
    const convertSubjectsInTaskIdeas = ((planningStatus, subjects) => {

        const subjectIds = subjects.map((subject) => subject.id);

        const path = `/api/me/tasks/planning/${ planningStatus.id }/subjects-ideas/convert`;

        return axios.post(path, {
            subjects: subjectIds
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.subjects) {
                subjectsIdeasByPlanning.value[planningStatus.id] = response.data.subjects;
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Initialise les données du store pour une nouvelle génération d'idées du planning en paramètre
     * @param object planning
     * @param int numberIdeas Nombre d'idées à générer
     * @return void
     */
    const initNewGenerationIdeas = ((planning, numberIdeas) => {
        lastIdeaGenerationByPlanning.value[planning.id] = {
            start: new Date(),
            end: null,
            totalExpected: numberIdeas,
            ideasFound: 0
        };
    });

    /**
     * Réinitialise la dernière exécution de générations des idées du planning en paramètre
     * @param object planning
     * @return void
     */
    const resetLastPlanningIdeaGenerationData = ((planning) => {
        delete lastIdeaGenerationByPlanning.value[planning.id];
    });

    /**
     * Modifie le nombre de tâches trouvées lors de la génération d'idées d'un planning
     * @param object planning
     * @param int countTasksFound
     * @return void
     */
    const setLastPlanningIdeaGenerationCountTasks = ((planning, countTasksFound) => {
        const data = (lastIdeaGenerationByPlanning.value[planning.id] ?? null);
        if(data === null) {
            return;
        }

        lastIdeaGenerationByPlanning.value[planning.id].ideasFound = countTasksFound;
    });

    return {
        getPlanningStatusByPeriod,
        getLastPlanningIdeaGenerationData,
        getPlanningStatusInitializationTaskListDate,
        getSubjectsIdeasByPlanning,
        setPlanningStatusInitializationTaskListDate,
        setLastPlanningIdeaGenerationCountTasks,
        updatePlanningStatus,
        generateNewIdeas,
        generate,
        complete,
        resetLastPlanningIdeaGenerationData,
        initNewGenerationIdeas,
        loadSubjectsIdeasOfPlanning,
        convertSubjectsInTaskIdeas,
        onRemoveSubjectOfPlanning
    };

}));
